import React, { useState, FC, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Flex from '../../Layout/Flex/Flex'
import Label from '../Label/Label'
import {
  InputMultipleNumbersWrapper,
  InputMultipleNumbersSectionMainWrapper,
  InputMultipleNumbersSectionWrapper,
  InputStyle,
  NumSectionSeparator,
  ErrorLabel,
} from './InputMultipleNumbers.styles'
import { InputMultipleNumbersProps } from './types'

const InputMultipleNumbers: FC<InputMultipleNumbersProps> = props => {
  const {
    label,
    numberFormat,
    error,
    value = '',
    onChange,
    required,
    name,
    isAlphaNumeric,
    lowerToUpperCase,
  } = props

  // eslint-disable-next-line
  const { t } = useTranslation<any>()

  // regex hard coded due a bug found in the on change flow, there was a need to type 2x times the char in order to insert it, it seems that the bug is related to the import of the regex
  const regex = isAlphaNumeric ? /^[a-zA-Z0-9]+$/g : /^[0-9]+$/g

  const numSectionHelper = useMemo(() => {
    if(typeof value !== 'string') return
    let helpValue = value === '' ? '' : value
    return numberFormat.split('-').map(numSec =>
      numSec.split('').map(() => {
        const first = helpValue?.[0] || ''
        if (value !== '') helpValue = helpValue.substring(1)
        return first
      })
    )
  }, [numberFormat])

  const inputRef = useRef([])
  const [valueStructure, setValueStructure] = useState(numSectionHelper)
  const [isError, setIsError] = useState('')

  const indexStructure = useMemo(() => {
    let currentIndex = 0
    return valueStructure.map(section => {
      return section.map(() => {
        currentIndex++
        return currentIndex - 1
      })
    })
  }, [])

  const checkIsError = (packedValue: string[]) => {
    const packedValueModified = packedValue.map(value =>
      value ? value : ' '
    )
    if (packedValueModified.join('').trimEnd().includes(' ')) {
      // @ts-ignore:next-line
      setIsError(t('error.invalidValue'))
    } else {
      setIsError('')
    }
  }

  useEffect(() => {
    const packedValue = valueStructure.reduce((previousValue, currentValue) =>
      previousValue.concat(currentValue)
    )
    checkIsError(packedValue)
    if (onChange)
      onChange({
        required,
        name: name,
        value: packedValue.includes('')
          ? required
            ? null
            : ''
          : packedValue.join(''),
      })
  }, [valueStructure])

  const onUpdate = (value: string, numSecIndex: number, numIndex: number) => {
    const valueStructureCopy = [...valueStructure]
    valueStructureCopy[numSecIndex as number][numIndex as number] = lowerToUpperCase ? value.toUpperCase() : value
    setValueStructure(valueStructureCopy)
  }

  const setFocusInput = (inputRefIndex: number) => {
    const nextEmptyIndex = inputRef?.current.reduce((prev, element, index) => {
      if (index < inputRefIndex) return null
      if (prev === null && element?.value === '') return index
      return prev
    }, null)
    if (nextEmptyIndex !== null) {
      inputRef.current?.[nextEmptyIndex as number]?.focus()
    }
  }

  const setBackFocusInput = (inputRefIndex: number) => {
    // Do not remove settimeout, because it is waiting for the currect event loop to end and empty the main stack
    setTimeout(() => {
      inputRef.current?.[(inputRefIndex as number) - 1]?.focus()
    }, 0)
  }

  const errorOutput = error || isError

  if (!numberFormat) {
    console.error(
      'You need to provide the number format. For example: 000000-00000-00-000'
    )
    return null
  }

  // @ts-ignore:next-line
  const keyPress = event => {
  // eslint-disable-next-line
    if (!regex.test(event?.key)){ 
      event.preventDefault()
    }
  }

  return (
    <InputMultipleNumbersWrapper gridArea={props?.gridArea} minHeight={props.minHeight} wrapperPadding={props.wrapperPadding}>
      <Label
        label={label}
        name={name}
        required={required}
        tooltipLabel={props.tooltipLabel}
        removeEllipsis={props.removeLabelEllipsis}
      />
      <Flex justifyContent='flex-start'>
        {valueStructure.map((numSec, numSecIndex) => (
          <InputMultipleNumbersSectionMainWrapper key={numSecIndex}>
            <InputMultipleNumbersSectionWrapper error={errorOutput}>
              {numSec.map((num, numIndex) => (
                <InputStyle
                  key={numIndex}
                  type='text'
                  ref={element =>
                    (inputRef.current[
                      indexStructure?.[numSecIndex as number]?.[
                        numIndex as number
                      ]
                    ] = element)
                  }
                  maxLength={1}
                  disabled={props.disabled}
                  value={num}
                  onChange={event => {
                    const value = event?.target?.value || ''
                    if (value !== '') {
                      setFocusInput(
                        indexStructure?.[numSecIndex as number]?.[
                          numIndex as number
                        ]
                      )
                    }
                    onUpdate(event.target.value, numSecIndex, numIndex)
                  }}
                  onKeyDown={event => {
                    // Backspace
                    if (event?.key === 'Backspace') {
                      setBackFocusInput(
                        indexStructure?.[numSecIndex as number]?.[
                          numIndex as number
                        ]
                      )
                    }
                  }}
                  onKeyPress={keyPress}
                />
              ))}
            </InputMultipleNumbersSectionWrapper>

            {numSecIndex < valueStructure.length - 1 && (
              <NumSectionSeparator>-</NumSectionSeparator>
            )}
          </InputMultipleNumbersSectionMainWrapper>
        ))}
      </Flex>
      {errorOutput && <ErrorLabel>{errorOutput}</ErrorLabel>}
    </InputMultipleNumbersWrapper>
  )
}

InputMultipleNumbers.defaultProps = {
  required: false,
  disabled: false,
  error: '',
  removeLabelEllipsis: false,
  value: '',
  numberFormat: ''
}

export default InputMultipleNumbers
